/**
 * LinkedInConnectionCard
 *
 * Lets a tenant paste their LinkedIn browser cookies so the platform's
 * linkedin-parser WASM skill can fetch JS-rendered profile pages via an
 * authenticated session.
 *
 * Connected state:
 *   Green "Connected" badge + connected-at timestamp + Disconnect button
 *
 * Disconnected state:
 *   Textarea for li_at cookie value or full DevTools JSON cookie array +
 *   Connect button + inline error
 *
 * API calls:
 *   GET  /v1/connections              → ConnectionsResponse
 *   POST /v1/connections/linkedin     → ConnectLinkedInResponse
 *   DELETE /v1/connections/linkedin   → clears state (404 handled gracefully)
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { api, apiErrorMessage } from '../api/client'
import type { ConnectionsResponse, ConnectLinkedInResponse } from '../api/types'

// ── LinkedIn SVG icon ─────────────────────────────────────────────────────────

function IconLinkedIn() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="w-[22px] h-[22px]" aria-hidden="true">
      <rect width="24" height="24" rx="4" fill="#0A66C2" />
      <path
        d="M7.2 9.6H4.8V19.2H7.2V9.6ZM6 8.4C6.796 8.4 7.44 7.756 7.44 6.96C7.44 6.164 6.796 5.52 6 5.52C5.204 5.52 4.56 6.164 4.56 6.96C4.56 7.756 5.204 8.4 6 8.4ZM19.44 13.56C19.44 11.28 18.28 9.36 16 9.36C14.96 9.36 14.08 9.84 13.52 10.52V9.6H11.2V19.2H13.6V14.04C13.6 12.92 14.32 12 15.44 12C16.56 12 17.04 12.88 17.04 14V19.2H19.44V13.56Z"
        fill="white"
      />
    </svg>
  )
}

// ── Spinner ───────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <span
      className="inline-block w-[13px] h-[13px] rounded-full border-2 border-[rgba(255,255,255,0.25)] border-t-current animate-spin flex-shrink-0"
      aria-hidden="true"
    />
  )
}

// ── LinkedInConnectionCard ────────────────────────────────────────────────────

type LoadState = 'loading' | 'ready' | 'error'

export function LinkedInConnectionCard() {
  const [loadState,    setLoadState]    = useState<LoadState>('loading')
  const [connected,    setConnected]    = useState(false)
  const [expiresAt,    setExpiresAt]    = useState<string | null>(null)
  const [connecting,   setConnecting]   = useState(false)
  const [disconnecting,setDisconnecting]= useState(false)
  const [showForm,     setShowForm]     = useState(false)
  const [cookieInput,  setCookieInput]  = useState('')
  const [inputError,   setInputError]   = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ── Load current status ──────────────────────────────────────────────────

  const loadStatus = useCallback(async () => {
    setLoadState('loading')
    try {
      const data = await api.get<ConnectionsResponse>('/v1/connections')
      setConnected(data.linkedin?.connected ?? false)
      setExpiresAt(data.linkedin?.expires_at ?? null)
      setLoadState('ready')
    } catch {
      setLoadState('error')
    }
  }, [])

  useEffect(() => { void loadStatus() }, [loadStatus])

  // Focus textarea when the form becomes visible
  useEffect(() => {
    if (showForm) {
      setTimeout(() => textareaRef.current?.focus(), 50)
    }
  }, [showForm])

  // ── Connect ──────────────────────────────────────────────────────────────

  const handleConnect = useCallback(async () => {
    const value = cookieInput.trim()
    if (!value) {
      setInputError('Paste your li_at cookie value or DevTools JSON array.')
      return
    }
    setInputError('')
    setConnecting(true)
    try {
      const res = await api.post<ConnectLinkedInResponse>(
        '/v1/connections/linkedin',
        { cookies: value },
      )
      setConnected(true)
      setExpiresAt(res.expires_at ?? null)
      setShowForm(false)
      setCookieInput('')
    } catch (err) {
      setInputError(apiErrorMessage(err, 'Failed to save session.'))
    } finally {
      setConnecting(false)
    }
  }, [cookieInput])

  // ── Disconnect ───────────────────────────────────────────────────────────

  const handleDisconnect = useCallback(async () => {
    setDisconnecting(true)
    try {
      await api.delete<void>('/v1/connections/linkedin')
    } catch {
      // 404 expected — DELETE endpoint not yet implemented; clear state anyway.
    } finally {
      setConnected(false)
      setExpiresAt(null)
      setShowForm(false)
      setCookieInput('')
      setDisconnecting(false)
    }
  }, [])

  // ── Render: loading skeleton ─────────────────────────────────────────────

  if (loadState === 'loading') {
    return (
      <div className="rounded-2xl bg-[rgba(10,18,28,0.72)] border border-[rgba(148,163,184,0.1)] p-[22px] pb-[18px] max-w-[480px] animate-pulse">
        <div className="w-[42px] h-[42px] rounded-xl bg-[rgba(148,163,184,0.08)] mb-4" />
        <div className="h-[14px] w-[140px] rounded bg-[rgba(148,163,184,0.08)] mb-2" />
        <div className="h-[11px] w-[260px] rounded bg-[rgba(148,163,184,0.06)]" />
      </div>
    )
  }

  // ── Render: load error ───────────────────────────────────────────────────

  if (loadState === 'error') {
    return (
      <div className="rounded-2xl bg-[rgba(10,18,28,0.72)] border border-[rgba(148,163,184,0.1)] p-[22px] pb-[18px] max-w-[480px]">
        <p className="text-[12.5px] text-[#f87171] flex items-center gap-2 m-0">
          <span aria-hidden="true">⚠</span>
          Failed to load connection status.{' '}
          <button
            className="bg-transparent border-none text-[#f87171] text-[12.5px] font-[inherit] cursor-pointer underline p-0 hover:text-[#fca5a5]"
            onClick={() => void loadStatus()}
          >
            Retry
          </button>
        </p>
      </div>
    )
  }

  // ── Render: ready ────────────────────────────────────────────────────────

  const isConnected = connected && !showForm

  return (
    <article
      className={[
        'flex flex-col gap-3 rounded-2xl p-[22px] pb-[18px] backdrop-blur-[10px] transition-all duration-[180ms] relative overflow-hidden border max-w-[480px]',
        isConnected
          ? 'bg-[rgba(10,18,28,0.80)] border-[rgba(34,197,94,0.25)] hover:border-[rgba(34,197,94,0.42)] hover:shadow-[0_4px_32px_rgba(34,197,94,0.08)]'
          : 'bg-[rgba(10,18,28,0.72)] border-[rgba(148,163,184,0.1)] hover:border-[rgba(148,163,184,0.22)]',
      ].join(' ')}
      aria-label="LinkedIn session connection"
    >
      {/* Icon */}
      <div
        className="w-[42px] h-[42px] flex items-center justify-center rounded-xl bg-[rgba(6,10,16,0.6)] border border-[rgba(148,163,184,0.1)] flex-shrink-0"
        aria-hidden="true"
      >
        <IconLinkedIn />
      </div>

      {/* Header */}
      <div className="flex items-baseline gap-2">
        <h3 className="text-[15px] font-[800] text-[#c8d9ee] m-0 tracking-[-0.02em]">LinkedIn</h3>
        <span className="text-[9.5px] font-bold tracking-[0.1em] uppercase text-[#3a5068] bg-[rgba(148,163,184,0.07)] border border-[rgba(148,163,184,0.1)] rounded-full px-[7px] py-0.5">
          Session cookie
        </span>
      </div>

      {/* Description */}
      <p className="text-[12px] text-[#4e6480] leading-[1.6] m-0">
        Paste your LinkedIn session cookies so the AI can fetch authenticated
        profile pages via the JS-rendering pipeline.
      </p>

      {/* Status badge */}
      <div className="min-h-6">
        {isConnected ? (
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-[6px] text-[11px] font-bold px-[9px] py-[3px] pl-[6px] rounded-full w-fit bg-[rgba(34,197,94,0.10)] border border-[rgba(34,197,94,0.25)] text-[#4ade80]">
              <span
                className="w-[6px] h-[6px] rounded-full flex-shrink-0 bg-[#4ade80]"
                aria-hidden="true"
                style={{ boxShadow: '0 0 0 3px rgba(74,222,128,0.25)' }}
              />
              Connected
            </span>
            {expiresAt && (
              <span className="text-[11px] text-[#4e6480] tabular-nums pl-[21px]">
                Expires {new Date(expiresAt).toLocaleDateString()}
              </span>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center gap-[6px] text-[11px] font-bold px-[9px] py-[3px] pl-[6px] rounded-full w-fit bg-[rgba(148,163,184,0.07)] border border-[rgba(148,163,184,0.12)] text-[#4e6480]">
            <span className="w-[6px] h-[6px] rounded-full flex-shrink-0 bg-[#3a5068]" aria-hidden="true" />
            {showForm ? 'Paste your cookies below' : 'Disconnected'}
          </span>
        )}
      </div>

      {/* Cookie input form — visible when disconnected or re-connecting */}
      {(!connected || showForm) && (
        <div className="flex flex-col gap-2 mt-1">
          <p className="text-[11px] text-[#4e6480] leading-[1.6] m-0 bg-[rgba(6,10,16,0.4)] border border-[rgba(148,163,184,0.1)] rounded-[8px] px-3 py-2.5">
            LinkedIn session cookies let the platform fetch authenticated profile data on your behalf.
            Your cookies are encrypted and never shared. To get your cookies: open LinkedIn in Chrome →
            F12 → Application → Cookies → linkedin.com → copy the{' '}
            <code className="font-mono text-[#8fa3bd]">li_at</code> value.
          </p>
          <label
            htmlFor="li-cookie-input"
            className="text-[11px] font-bold text-[#8fa3bd] tracking-[0.04em]"
          >
            li_at cookie value or full DevTools JSON array
          </label>
          <textarea
            id="li-cookie-input"
            ref={textareaRef}
            value={cookieInput}
            onChange={(e) => { setCookieInput(e.target.value); setInputError('') }}
            placeholder={'li_at=AQEDATxxxxxxxxxx…\nor\n[{"name":"li_at","value":"…","domain":".linkedin.com","path":"/"}]'}
            rows={4}
            spellCheck={false}
            className={[
              'w-full resize-y rounded-[9px] border bg-[rgba(6,10,16,0.55)] text-[12px] text-[#c8d9ee] font-mono leading-[1.55] px-[12px] py-[10px] outline-none transition-colors duration-[120ms] placeholder:text-[#2c3f55]',
              inputError
                ? 'border-[rgba(239,68,68,0.45)] focus:border-[rgba(239,68,68,0.7)]'
                : 'border-[rgba(148,163,184,0.15)] focus:border-[rgba(56,189,248,0.4)]',
            ].join(' ')}
            aria-describedby={inputError ? 'li-cookie-error' : undefined}
            aria-invalid={!!inputError}
          />
          {inputError && (
            <p id="li-cookie-error" className="text-[11.5px] text-[#f87171] m-0 flex items-center gap-[5px]">
              <span aria-hidden="true">⚠</span>
              {inputError}
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-0.5">
        {isConnected ? (
          <>
            <button
              className="inline-flex items-center justify-center gap-[6px] px-[14px] py-2 rounded-[9px] border border-[rgba(148,163,184,0.18)] bg-[rgba(148,163,184,0.08)] text-[#8fa3bd] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:bg-[rgba(148,163,184,0.14)] hover:text-[#c8d9ee] disabled:opacity-[0.55] disabled:cursor-not-allowed"
              onClick={() => { setShowForm(true); setCookieInput(''); setInputError('') }}
              disabled={disconnecting}
            >
              Replace session
            </button>
            <button
              className="inline-flex items-center justify-center gap-[6px] px-[11px] py-[6px] rounded-[9px] border border-[rgba(239,68,68,0.22)] bg-transparent text-[#f87171] font-[inherit] text-[11px] font-bold cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:bg-[rgba(239,68,68,0.08)] hover:border-[rgba(239,68,68,0.4)] disabled:opacity-[0.55] disabled:cursor-not-allowed"
              onClick={() => void handleDisconnect()}
              disabled={disconnecting}
            >
              {disconnecting ? <Spinner /> : null}
              {disconnecting ? 'Removing…' : 'Disconnect'}
            </button>
          </>
        ) : showForm ? (
          <>
            <button
              className="flex-1 inline-flex items-center justify-center gap-[6px] px-[14px] py-2 rounded-[9px] border border-[rgba(56,189,248,0.35)] bg-[rgba(56,189,248,0.14)] text-[#38bdf8] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:bg-[rgba(56,189,248,0.24)] hover:border-[rgba(56,189,248,0.55)] disabled:opacity-[0.55] disabled:cursor-not-allowed active:scale-[0.97]"
              onClick={() => void handleConnect()}
              disabled={connecting}
            >
              {connecting ? <Spinner /> : null}
              {connecting ? 'Saving…' : 'Save session'}
            </button>
            <button
              className="inline-flex items-center justify-center gap-[6px] px-[11px] py-[6px] rounded-[9px] border border-[rgba(148,163,184,0.15)] bg-transparent text-[#4e6480] font-[inherit] text-[11px] font-bold cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:text-[#8fa3bd] hover:border-[rgba(148,163,184,0.28)] disabled:opacity-[0.55]"
              onClick={() => { setShowForm(false); setCookieInput(''); setInputError('') }}
              disabled={connecting}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            className="flex-1 inline-flex items-center justify-center gap-[6px] px-[14px] py-2 rounded-[9px] border border-[rgba(56,189,248,0.35)] bg-[rgba(56,189,248,0.14)] text-[#38bdf8] font-[inherit] text-[12px] font-[800] tracking-[0.02em] cursor-pointer transition-all duration-[130ms] min-h-[34px] whitespace-nowrap hover:bg-[rgba(56,189,248,0.24)] hover:border-[rgba(56,189,248,0.55)] disabled:opacity-[0.55] disabled:cursor-not-allowed active:scale-[0.97]"
            onClick={() => { setShowForm(true); setCookieInput(''); setInputError('') }}
            disabled={connecting}
          >
            Connect
          </button>
        )}
      </div>
    </article>
  )
}
